import { DateTimeRangePicker as MuiDateTimeRangePicker, DateTimeRangePickerProps } from '@mui/x-date-pickers-pro';
import { Dayjs } from 'dayjs';
import { AppInputCustomProps, CustomFieldProps } from './types';
import { useEffect, useMemo, useState } from 'react';
import { parseDayJsRange } from '@/Infrastructure/Utilities/dayjs.type';
import useDebouncer from '@hooks/useDebounce';
import SegmentedDateTimeRangeField from './SegmentedDateTimeRangeField';

export type DateTimePickerRangeValue = [Dayjs | null, Dayjs | null];

/**
 * Wrapper around MUI DateTimeRangePicker using a custom SegmentedDateTimeRangeField
 * that renders the compact format MM/DD/YY HH:mm - HH:mm with segment-by-segment
 * arrow navigation and a calendar/time picker popup.
 */
export default function DateTimeRangePicker({ customProps, ...componentProps }: DateTimeRangePickerProps & AppInputCustomProps) {
  return (
    <MuiDateTimeRangePicker
      {...componentProps}
      slots={{
        field: SegmentedDateTimeRangeField as never
      }}
      slotProps={{
        field: {
          isDirty: customProps.isDirty,
          isError: customProps.isError,
          errorText: customProps.errorText,
          labelText: customProps.labelText,
          variant: customProps.variant || 'outlined',
          onClear: customProps.onClear,
          required: customProps.required,
          disabledReason: customProps.disabledReason,
          disabledClass: customProps.disabledClass,
          onBlurField: customProps.onBlur
        } as never
      }}
    />
  );
}

/**
 * Convert a [Dayjs, Dayjs] range to the string format "MM/DD/YY HH:mm - HH:mm"
 * used by the Formik form values.
 */
function rangeToString(range: DateTimePickerRangeValue): string | null {
  const [from, to] = range;
  if (!from?.isValid()) return null;
  const fromStr = from.format('MM/DD/YY HH:mm');
  if (!to?.isValid()) return fromStr;
  const toStr = to.format('HH:mm');
  return `${fromStr} - ${toStr}`;
}

/**
 * Convert the string format "MM/DD/YY HH:mm - HH:mm" to a [Dayjs, Dayjs] range
 * for the MUI DateTimeRangePicker value.
 */
function stringToRange(value: string | null): DateTimePickerRangeValue {
  const result = parseDayJsRange(value);
  return [result[0] ?? null, result[1] ?? null];
}

/**
 * Formik-compatible DateTimeRangePicker that stores its value as a string
 * in the format "MM/DD/YY HH:mm - HH:mm" for compatibility with existing
 * form types, validation, and API mapping functions.
 */
function asDateTimeRangePicker() {
  return ({ field, form, ...props }: CustomFieldProps) => {
    const { value, initialValue, error } = form.getFieldMeta<string | null>(field.name);

    const { dirty, validateForm } = form;

    const isDirty = useMemo(() => {
      if (dirty) {
        return value !== initialValue;
      }
      return false;
    }, [value, initialValue, dirty]);

    const [localRange, setLocalRange] = useState<DateTimePickerRangeValue>(() => stringToRange(value));

    useEffect(() => {
      setLocalRange((state) => {
        const newRange = stringToRange(value);
        const stateStr = rangeToString(state);
        if (value !== stateStr) {
          return newRange;
        }
        return state;
      });
      void validateForm();
    }, [value, validateForm]);

    useDebouncer(
      () => {
        const newStringValue = rangeToString(localRange);
        if (newStringValue !== value) {
          void form.setFieldTouched(field.name, true);
          void form.setFieldValue(field.name, newStringValue, true);
          void validateForm();
        }
      },
      [localRange],
      300
    );

    return (
      <DateTimeRangePicker
        name={field.name}
        className={props.className}
        value={localRange}
        onChange={(newValue) => {
          setLocalRange(newValue);
        }}
        disabled={props.disabled}
        customProps={{
          labelText: props.label ?? '',
          isDirty: isDirty,
          isError: Boolean(error),
          errorText: error,
          onClear: () => {
            setLocalRange([null, null]);
          },
          disabledReason: props.disabledReason,
          required: props.required
        }}
      />
    );
  };
}

export const FormikDateTimeRangePicker = asDateTimeRangePicker();
